// Shared audit_log rendering — one place both the employee detail page's
// History section and the admin-only org-wide Audit page read, so the two
// views can never describe the same action differently. Mirrors the
// AuditAction* constants + writeAudit call sites in internal/models/
// models.go and internal/api/handlers/{audit,attendance_sweep,hr_attendance,
// employee_profile,leave,hr_users,hr_work_zones}.go.

export const AUDIT_ACTION_LABEL: Record<string, string> = {
  auto_closed: 'Auto-closed (forgot punch-out)',
  leave_deducted: 'Leave day deducted',
  punch_out_fixed: 'Punch-out fixed',
  manual_attendance_recorded: 'Manual attendance recorded',
  counter_reset: 'Warning counter reset',
  leave_adjusted: 'Leave adjusted',
  profile_updated: 'Profile updated',
  document_added: 'Document added',
  document_removed: 'Document removed',
  leave_type_saved: 'Leave type saved',
  leave_type_deleted: 'Leave type deleted',
  hr_request_leave_type_set: 'Leave type set on request',
  user_created: 'User created',
  user_updated: 'User updated',
  password_reset: 'Password reset',
  mfa_reset: 'MFA reset',
  work_zones_assigned: 'Site assignments changed',
  work_zone_created: 'Site created',
  work_zone_updated: 'Site updated',
  work_zone_deleted: 'Site deleted',
  hr_flags_viewed: 'Attendance flags viewed'
};

function fieldLabel(key: string): string {
  return key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' ');
}

function fmtVal(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v)) return new Date(v).toLocaleString();
  return String(v);
}

// humanizeAuditDetails turns one audit_log row's action + JSON details blob
// into a single readable line, in the interface's plain-terms voice — never
// raw JSON, unless the action is one this codebase hasn't defined yet
// (future-proofing, not expected to actually happen).
export function humanizeAuditDetails(action: string, details: Record<string, unknown>): string {
  switch (action) {
    case 'profile_updated':
    case 'user_updated':
    case 'work_zone_updated': {
      const changes = (details.changes ?? {}) as Record<string, { old: unknown; new: unknown }>;
      const cols = Object.keys(changes);
      if (cols.length === 0) return 'No fields changed.';
      return cols.map((c) => `${fieldLabel(c)}: ${fmtVal(changes[c]?.old)} → ${fmtVal(changes[c]?.new)}`).join('; ');
    }
    case 'user_created':
      return `${details.name ?? '—'} <${details.email ?? '—'}> (${details.role ?? '—'})`;
    case 'password_reset':
    case 'mfa_reset':
      return '—';
    case 'work_zones_assigned': {
      const oldIDs = (details.old ?? []) as number[];
      const newIDs = (details.new ?? []) as number[];
      return `${oldIDs.length} site(s) → ${newIDs.length} site(s)`;
    }
    case 'work_zone_created':
      return `${details.name ?? '—'} (${fmtVal(details.radius)}m radius)`;
    case 'work_zone_deleted':
      return `${details.name ?? '—'}`;
    case 'document_added':
    case 'document_removed':
      return `${details.filename ?? '—'}${details.label ? ` (${details.label})` : ''}`;
    case 'punch_out_fixed':
      return `New punch-out ${fmtVal(details.new_punch_out)}${details.leave_reversed ? ' — leave day reversed' : ''}`;
    case 'manual_attendance_recorded':
      return `HR-entered ${fmtVal(details.punch_in)}${details.punch_out ? ` → ${fmtVal(details.punch_out)}` : ' (open)'} — ${details.reason ?? '—'}`;
    case 'counter_reset':
      return `Reset at ${fmtVal(details.reset_at)}`;
    case 'leave_adjusted': {
      const days = details.days as number | undefined;
      const sign = days != null && days > 0 ? '+' : '';
      return `${sign}${days ?? '?'} day(s) — ${details.note ?? '—'}`;
    }
    case 'leave_type_saved':
      return `${details.action ?? 'saved'}: ${details.name ?? '—'}`;
    case 'leave_type_deleted':
      return `${details.name ?? '—'}`;
    case 'hr_request_leave_type_set':
      return `Request #${details.request_id ?? '—'} → ${details.name ?? '—'}`;
    case 'auto_closed':
      return `Work log #${details.work_log_id ?? '—'} auto-closed`;
    case 'leave_deducted':
      return `1 day deducted (occurrence ${details.occurrence ?? '?'})`;
    default:
      return JSON.stringify(details);
  }
}
